import type { ReactNode } from "react";

export type MicroMark = "arrow" | "check" | "warn" | "plus" | "minus" | "dot" | "none";

type Tone =
  | "default"
  | "muted"
  | "accent"
  | "primary"
  | "onDark"
  | "onDarkSoft"
  | "onDarkAccent"
  | "onDarkMuted"
  | "onDarkDim"
  | "onDarkPrimary";

const MARKS: Record<MicroMark, string> = {
  arrow: "→",
  check: "✓",
  warn: "!",
  plus: "+",
  minus: "−",
  dot: "·",
  none: "",
};

const TONES: Record<Tone, string> = {
  default: "text-foreground-950",
  muted: "text-foreground-600",
  accent: "text-accent-800",
  primary: "text-primary-800",
  onDark: "text-background-50",
  onDarkSoft: "text-background-300",
  onDarkAccent: "text-accent-300",
  onDarkMuted: "text-background-400",
  onDarkDim: "text-background-500",
  onDarkPrimary: "text-primary-500",
};

interface MicroLabelProps {
  children: ReactNode;
  mark?: MicroMark;
  tone?: Tone;
  className?: string;
}

/**
 * The site's single small-label voice: one fixed marker slot, one size,
 * one tracking value. Every callout label and strip label goes through it.
 */
export default function MicroLabel({
  children,
  mark = "none",
  tone = "default",
  className = "",
}: MicroLabelProps) {
  return (
    <span
      className={`inline-flex items-center gap-2 font-label text-[10px] uppercase tracking-[0.18em] font-semibold leading-none ${TONES[tone]} ${className}`}
    >
      {mark !== "none" && (
        <span
          className="w-3.5 shrink-0 text-center font-heading font-black text-[13px] leading-none"
          aria-hidden="true"
        >
          {MARKS[mark]}
        </span>
      )}
      {children}
    </span>
  );
}